import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Smartphone, Mail, ChevronDown, ArrowRight, Loader2 } from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';
import Link from 'next/link';

/**
 * Login Method Tabs Component
 */
export const LoginMethodTabs = ({ isLoginWithEmail, setIsLoginWithEmail, emailFirst = false }) => {
    const tabs = emailFirst 
        ? [
            { key: 'email', label: 'Email', icon: <Mail size={16} />, active: isLoginWithEmail },
            { key: 'mobile', label: 'Mobile', icon: <Smartphone size={16} />, active: !isLoginWithEmail }
          ]
        : [
            { key: 'mobile', label: 'Mobile', icon: <Smartphone size={16} />, active: !isLoginWithEmail },
            { key: 'email', label: 'Email', icon: <Mail size={16} />, active: isLoginWithEmail }
          ];

    return (
        <div className="flex bg-gray-100 rounded-xl p-1">
            {tabs.map((tab) => (
                <button 
                    key={tab.key}
                    onClick={() => setIsLoginWithEmail(tab.key === 'email')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        tab.active 
                            ? 'bg-white text-gray-900 shadow-sm' 
                            : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    {tab.icon}
                    {tab.label}
                </button>
            ))}
        </div>
    );
};

/**
 * Mobile Number Input Form Component
 */
export const MobileNumberForm = ({ 
    onSubmit, 
    mobileNumber, 
    onMobileNumberChange, 
    mobileNumberError, 
    loginTelCd, 
    loginCountryCd, 
    onCountrySelect, 
    isLoading 
}) => (
    <form onSubmit={onSubmit} className="space-y-4">
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number
            </label>
            <div className="flex gap-2">
                <button 
                    type="button" 
                    onClick={onCountrySelect}
                    className="flex items-center gap-1.5 px-3 py-3 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors"
                >
                    <ReactCountryFlag
                        countryCode={loginCountryCd}
                        svg
                        style={{ width: '1.2em', height: '1.2em' }}
                    />
                    <span className="text-gray-700">{loginTelCd}</span>
                    <ChevronDown size={14} className="text-gray-400" />
                </button>
                <input
                    type="number"
                    className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-all"
                    placeholder="Enter mobile number"
                    value={mobileNumber}
                    onChange={onMobileNumberChange}
                    required
                />
            </div>
            <p className="mt-2 text-xs text-gray-500">
                {mobileNumberError || "Code will be sent to your mobile and registered email"}
            </p>
        </div>
        
        <SubmitButton 
            disabled={!!mobileNumberError || isLoading}
            isLoading={isLoading}
            icon={<ArrowRight size={20} />}
        >
            Send Code
        </SubmitButton>
    </form>
);

/**
 * OTP Verification Form Component
 */
export const OtpVerificationForm = ({ 
    onSubmit, 
    otp, 
    onOtpChange, 
    loginTelCd, 
    mobileNumber, 
    isLoading, 
    onBack 
}) => (
    <form onSubmit={onSubmit} className="space-y-4">
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter OTP
            </label>
            <input
                type="number"
                className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-center text-xl tracking-[0.5em] font-mono text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-all"
                placeholder="••••••"
                value={otp}
                onChange={onOtpChange}
                required
            />
            <p className="mt-2 text-xs text-gray-500 text-center">
                OTP sent to {loginTelCd} {mobileNumber}
            </p>
        </div>
        
        <SubmitButton disabled={isLoading} isLoading={isLoading}>
            Verify OTP
        </SubmitButton>
        
        <button
            type="button"
            onClick={onBack}
            className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
            ← Back to mobile number
        </button>
    </form>
);

/**
 * Email Login Form Component
 */
export const EmailLoginForm = ({ 
    onSubmit, 
    emailId, 
    password, 
    onEmailChange, 
    onPasswordChange, 
    passwordType, 
    onTogglePassword, 
    isLoading 
}) => (
    <form onSubmit={onSubmit} className="space-y-4">
        <div>
            <input
                type="email"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-all"
                placeholder="john@example.com"
                name="emailId"
                value={emailId}
                onChange={onEmailChange}
                required
            />
        </div>
        
        <div>
            <div className="relative">
                <input
                    type={passwordType}
                    className="w-full px-4 py-3 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-all"
                    placeholder="Enter password"
                    name="password"
                    value={password}
                    onChange={onPasswordChange}
                    required
                />
                <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                    onClick={onTogglePassword}
                >
                    {passwordType === "password" ? <Eye size={20} /> : <EyeOff size={20} />}
                </button>
            </div>
        </div>
        
        <div className="flex justify-end">
            <Link href="/forgot-password" className="text-sm text-orange-600 hover:text-orange-700 font-medium">
                Forgot Password?
            </Link>
        </div>
        
        <SubmitButton disabled={isLoading} isLoading={isLoading}>
            Sign In
        </SubmitButton>
    </form>
);

/**
 * Submit Button Component
 */
export const SubmitButton = ({ children, disabled, isLoading, icon, className = '' }) => (
    <motion.button
        whileTap={{ scale: 0.98 }}
        disabled={disabled}
        type="submit"
        className={`w-full py-3.5 bg-linear-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-xl shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 ${className}`}
    >
        {isLoading ? <Loader2 size={20} className="animate-spin" /> : icon}
        {children}
    </motion.button>
);

/**
 * Divider Component
 */
export const OrDivider = ({ text = "or continue with" }) => (
    <div className="relative">
        <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-400">{text}</span>
        </div>
    </div>
);

/**
 * Animated Form Container for switching between forms
 * Uses opacity-only animations to prevent CLS
 */
export const AnimatedFormContainer = ({ children, formKey, direction = 'left' }) => (
    <motion.div
        key={formKey}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
    >
        {children}
    </motion.div>
);
